import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Link2,
  Timer,
  Turtle,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useCaseQuery } from "hooks/useCaseDashboard";
import { parseUtc } from "utils/caseDashboard";
import {
  timingStats,
  wearableTimelines,
  pairingProblems,
  pairingRuns,
  playerNamesByMac,
  successRate,
  TIMING_STEPS,
} from "utils/caseDashboardPairing";
import {
  Card,
  EmptyState,
  QueryState,
  fmtTime,
  fmtDateTime,
  fmtShortMac,
} from "./parts";

// One colour per step of a pairing, in order.
const STEP_COLORS = {
  sighted_to_held: "#3B82F6",
  held_to_cmd: "#A855F7",
  cmd_queued: "#F59E0B",
  assign: "#10B981",
};
const OUTCOME_STYLE = {
  assigned: "bg-[#DCFCE7] text-[#15803D]",
  failed: "bg-[#FEE2E2] text-[#B91C1C]",
  pending: "bg-[#F3F4F6] text-[#6B7280]",
};
const EVENT_DOT = {
  wearable: "#9CA3AF",
  held: "#3B82F6",
  "cmd:assign": "#A855F7",
  assign_started: "#F59E0B",
  assigned: "#10B981",
  timing: "#10B981",
  assign_failed: "#DC2626",
  assign_warning: "#E07A3A",
};
const KNOWN_EVENTS = [
  "wearable",
  "held",
  "assign_started",
  "assigned",
  "assign_failed",
  "assign_warning",
  "hold_released",
  "wearable_gone",
  "discarded",
  "timing",
];

const PairingView = ({ caseId }) => {
  const { t } = useTranslation();
  const events = useCaseQuery(caseId, "pairing", { hours: 168 }, 5000);
  const rows = useMemo(() => events.data || [], [events.data]);
  const stats = useMemo(() => timingStats(rows), [rows]);
  const runs = useMemo(() => pairingRuns(rows), [rows]);
  const names = useMemo(() => playerNamesByMac(rows), [rows]);
  const timelines = useMemo(() => wearableTimelines(rows), [rows]);
  const problems = useMemo(() => pairingProblems(rows), [rows]);
  const rate = useMemo(() => successRate(rows), [rows]);
  const [open, setOpen] = useState(null);
  const secs = (v) =>
    v == null
      ? "—"
      : t("caseDashboard.pairing.seconds", { value: Number(v).toFixed(1) });
  const who = (mac) =>
    names[String(mac || "").toUpperCase()] || fmtShortMac(mac);

  if (!rows.length) {
    return (
      <div className="space-y-4">
        <QueryState query={events} />
        {!events.missing && (
          <Card>
            <EmptyState
              icon={Link2}
              title={
                events.loading
                  ? t("caseDashboard.common.loading")
                  : t("caseDashboard.pairing.empty")
              }
              body={
                events.loading ? null : t("caseDashboard.pairing.emptyHint")
              }
            />
          </Card>
        )}
      </div>
    );
  }

  const chartData = runs.map((r, i) => ({
    i: i + 1,
    label: r.player || fmtShortMac(r.mac),
    ...Object.fromEntries(TIMING_STEPS.map((s) => [s, r[s] ?? 0])),
    total: r.total,
    ts: r.ts,
  }));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-[22px] font-bold text-[#111827]">
          {t("caseDashboard.tabs.pairing")}
        </h1>
        <p className="mt-0.5 text-[13px] text-[#6B7280]">
          {t("caseDashboard.pairing.subtitle")}
        </p>
      </div>
      <QueryState query={events} />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          icon={Link2}
          label={t("caseDashboard.pairing.count")}
          value={stats.count}
        />
        <Kpi
          icon={Timer}
          label={t("caseDashboard.pairing.median")}
          value={secs(stats.median)}
          hint={t("caseDashboard.pairing.medianHint")}
        />
        <Kpi
          icon={Turtle}
          label={t("caseDashboard.pairing.slowest")}
          value={secs(stats.slowest?.total)}
          hint={stats.slowest ? who(stats.slowest.mac) : null}
        />
        <Kpi
          icon={CheckCircle2}
          label={t("caseDashboard.pairing.success")}
          value={rate == null ? "—" : `${Math.round(rate * 100)}%`}
          hint={
            problems.length
              ? t("caseDashboard.pairing.problemCount", {
                  count: problems.length,
                })
              : null
          }
          tone={rate != null && rate < 0.9 ? "warn" : undefined}
        />
      </div>

      {runs.length > 0 && (
        <div className="rounded-xl border border-[#F3F4F6] bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#F3F4F6] px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold text-[#111827]">
                {t("caseDashboard.pairing.everyPairing")}
              </h2>
              <p className="mt-0.5 text-[12px] text-[#6B7280]">
                {t("caseDashboard.pairing.everyPairingHint")}
              </p>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {TIMING_STEPS.map((s) => (
                <span
                  key={s}
                  className="flex items-center gap-1.5 text-[12px] text-[#4B5563]"
                >
                  <span
                    className="h-2.5 w-2.5 rounded-sm"
                    style={{ backgroundColor: STEP_COLORS[s] }}
                  />
                  {t(`caseDashboard.pairing.step.${s}`)}
                  <span className="font-semibold text-[#111827]">
                    {secs(stats.steps[s])}
                  </span>
                </span>
              ))}
            </div>
          </div>
          <div
            className="px-2 pb-2 pt-3"
            style={{
              height: Math.max(180, Math.min(420, chartData.length * 26 + 50)),
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 0, right: 24, bottom: 0, left: 8 }}
                barCategoryGap={4}
              >
                <CartesianGrid stroke="#F3F4F6" horizontal={false} />
                <XAxis
                  type="number"
                  unit=" s"
                  tick={{ fontSize: 11, fill: "#9CA3AF" }}
                  tickLine={false}
                  axisLine={{ stroke: "#E5E7EB" }}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={120}
                  tick={{ fontSize: 12, fill: "#374151" }}
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                />
                <Tooltip
                  cursor={{ fill: "#FAFAFA" }}
                  isAnimationActive={false}
                  content={<RunTooltip secs={secs} />}
                />
                {TIMING_STEPS.map((s, i) => (
                  <Bar
                    key={s}
                    dataKey={s}
                    stackId="t"
                    fill={STEP_COLORS[s]}
                    isAnimationActive={false}
                    radius={i === TIMING_STEPS.length - 1 ? [0, 3, 3, 0] : 0}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {problems.length > 0 && (
        <Card title={t("caseDashboard.pairing.problems")}>
          {problems.slice(0, 20).map((p, i) => (
            <div
              key={`${p.ts}-${i}`}
              className="flex items-start justify-between gap-3 border-b border-[#F3F4F6] px-4 py-2.5 last:border-b-0"
            >
              <span className="flex min-w-0 items-start gap-2">
                <AlertTriangle
                  size={14}
                  className="mt-0.5 shrink-0 text-[#E07A3A]"
                />
                <span className="min-w-0">
                  <span className="block text-[13px] font-medium text-[#111827]">
                    {who(p.mac)} · {t(`caseDashboard.pairing.event.${p.type}`)}
                  </span>
                  {p.message && (
                    <span className="block truncate text-xs text-[#6B7280]">
                      {p.message}
                    </span>
                  )}
                </span>
              </span>
              <span className="shrink-0 text-[11px] text-[#9CA3AF]">
                {fmtDateTime(p.ts)}
              </span>
            </div>
          ))}
        </Card>
      )}

      <Card title={t("caseDashboard.pairing.wearables")}>
        {timelines.map((w) => {
          const start = parseUtc(w.events[0]?.ts)?.getTime();
          const timing = [...w.events]
            .reverse()
            .find((e) => e.type === "timing");
          return (
            <div
              key={w.mac}
              className="border-b border-[#F3F4F6] last:border-b-0"
            >
              <button
                type="button"
                onClick={() => setOpen(open === w.mac ? null : w.mac)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition duration-150 hover:bg-[#FAFAFA]"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      OUTCOME_STYLE[w.outcome]
                    }`}
                  >
                    {t(`caseDashboard.pairing.outcome.${w.outcome}`)}
                  </span>
                  <span className="truncate text-sm font-medium text-[#111827]">
                    {names[w.mac] || w.name || fmtShortMac(w.mac)}
                  </span>
                  <span className="hidden font-mono text-[11px] text-[#9CA3AF] sm:inline">
                    {w.name || w.mac}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-3">
                  {timing?.detail?.total != null && (
                    <span className="text-[12px] font-semibold tabular-nums text-[#374151]">
                      {secs(timing.detail.total)}
                    </span>
                  )}
                  <span className="text-[11px] text-[#9CA3AF]">
                    {fmtDateTime(w.lastTs)}
                  </span>
                  <ChevronDown
                    size={14}
                    className={`text-[#9CA3AF] transition duration-150 ${
                      open === w.mac ? "rotate-180" : ""
                    }`}
                  />
                </span>
              </button>
              {open === w.mac && (
                <ol className="mb-3 ml-7 mr-4 border-l border-[#E5E7EB] pl-4">
                  {w.events.map((e, i) => {
                    const at = parseUtc(e.ts)?.getTime();
                    return (
                      <li
                        key={`${e.ts}-${i}`}
                        className="relative py-1 text-[13px]"
                      >
                        <span
                          className="absolute -left-[21px] top-2.5 h-2 w-2 rounded-full"
                          style={{
                            backgroundColor: EVENT_DOT[e.type] || "#E5E7EB",
                          }}
                        />
                        <span className="mr-2 inline-block w-16 text-[11px] tabular-nums text-[#9CA3AF]">
                          {start != null && at != null
                            ? `+${((at - start) / 1000).toFixed(1)} s`
                            : fmtTime(e.ts)}
                        </span>
                        <span className="text-[#374151]">
                          {KNOWN_EVENTS.includes(e.type)
                            ? t(`caseDashboard.pairing.event.${e.type}`)
                            : e.type.startsWith("cmd:")
                            ? t("caseDashboard.pairing.event.cmd", {
                                action: e.type.slice(4),
                              })
                            : e.type}
                        </span>
                        {e.type === "timing" && e.detail?.total != null && (
                          <span className="ml-2 text-xs text-[#6B7280]">
                            {secs(e.detail.total)}
                          </span>
                        )}
                        {e.detail?.rssi != null && e.type === "wearable" && (
                          <span className="ml-2 text-xs text-[#6B7280]">
                            {e.detail.rssi} dBm
                          </span>
                        )}
                        {e.detail?.error && (
                          <span className="ml-2 text-xs text-[#B91C1C]">
                            {e.detail.error}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          );
        })}
      </Card>
    </div>
  );
};

const RunTooltip = ({ active, payload, secs }) => {
  const { t } = useTranslation();
  const d = active && payload?.[0]?.payload;
  if (!d) return null;
  return (
    <div className="rounded-lg border border-[#F3F4F6] bg-white px-3 py-2 text-[12px] shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
      <p className="font-semibold text-[#111827]">
        {d.label} · {secs(d.total)}
      </p>
      <p className="mb-1 text-[11px] text-[#9CA3AF]">{fmtDateTime(d.ts)}</p>
      {TIMING_STEPS.map((s) => (
        <p
          key={s}
          className="flex items-center justify-between gap-4 text-[#4B5563]"
        >
          <span className="flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-sm"
              style={{ backgroundColor: STEP_COLORS[s] }}
            />
            {t(`caseDashboard.pairing.step.${s}`)}
          </span>
          <span className="tabular-nums">{secs(d[s])}</span>
        </p>
      ))}
    </div>
  );
};

const Kpi = ({ icon: Icon, label, value, hint, tone }) => (
  <div className="rounded-xl border border-[#F3F4F6] bg-white p-4">
    <div className="mb-2 flex items-center gap-1.5">
      <Icon size={14} className="text-[#6B7280]" />
      <span className="text-xs text-[#6B7280]">{label}</span>
    </div>
    <span
      className={`text-[28px] font-bold tabular-nums leading-none ${
        tone === "warn" ? "text-[#E07A3A]" : "text-[#111827]"
      }`}
    >
      {value}
    </span>
    {hint && (
      <p className="mt-1.5 truncate text-[12px] text-[#6B7280]">{hint}</p>
    )}
  </div>
);

export default PairingView;
